import React, { useState, useMemo, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  Collapse,
  IconButton,
  styled,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import SearchInput from "../../../Overview/components/SearchInput";
import CustomCheckbox from "../../../Overview/components/Checkbox";
import PrimaryChip from "../../../../Components/PrimaryChip";
import { toTitleCase } from "../../../../Helper/Formatters/TextFormatter";
import { PrimaryFormActionButton } from "../../../../Components/StyledComponent";

const StyledListItem = styled(ListItem)(() => ({
  paddingLeft: 0,
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ hasChildren, isLastNode }) => ({
  marginLeft: hasChildren ? "6px" : isLastNode ? "calc(6px + 10px)" : "calc(6px + 22px)",
}));

const ExpandCollapseIcon = styled(ArrowRightIcon)(({ isExpanded }) => ({
  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
  fontSize: "22px",
}));

const deepClone = (v) => JSON.parse(JSON.stringify(v));

/* Helper: Convert transformed format (with children) back to internal format (with states/cities/depots) */
const convertFromTransformedFormat = (transformedSelection) => {
  if (!transformedSelection || transformedSelection.length === 0) {
    return [];
  }

  return transformedSelection.map(country => {
    const result = { country: country.country };

    if (country.children && country.children.length > 0) {
      result.states = country.children.map(state => {
        const stateResult = { state: state.state };

        if (state.children && state.children.length > 0) {
          stateResult.cities = state.children.map(city => {
            const cityResult = { name: city.city };

            if (city.children && city.children.length > 0) {
              cityResult.depots = city.children.map(depot => depot.depot);
            } else {
              cityResult.depots = [];
            }

            return cityResult;
          });
        } else {
          stateResult.cities = [];
        }

        return stateResult;
      });
    } else {
      result.states = [];
    }

    return result;
  });
};

/* Helper: Convert internal format (with states/cities/depots) to transformed format (with children) */
const convertToTransformedFormat = (internalSelection) => {
  if (!internalSelection || internalSelection.length === 0) {
    return [];
  }

  return internalSelection.map(country => {
    const result = { country: country.country };

    if (country.states && country.states.length > 0) {
      result.children = country.states.map(state => {
        const stateResult = { state: state.state };

        if (state.cities && state.cities.length > 0) {
          stateResult.children = state.cities.map(city => {
            const cityResult = { city: city.name };

            // Only add children if there are depots
            if (city.depots && city.depots.length > 0) {
              cityResult.children = city.depots.map(depot => ({ depot }));
            }

            return cityResult;
          });
        }

        return stateResult;
      });
    }

    return result;
  });
};

function filterLocations(data, query) {
  if (!query) return data;
  const q = query.toLowerCase();

  return data
    .map((country) => {
      const states = country.states
        .map((state) => {
          const cities = state.cities
            .map((city) => {
              const depotMatches = city.depots.filter((d) =>
                d.toLowerCase().includes(q)
              );

              if (city.name.toLowerCase().includes(q)) {
                return { ...city, depots: depotMatches };
              } else if (depotMatches.length > 0) {
                return { ...city, depots: depotMatches };
              }

              return null;
            })
            .filter(Boolean);

          if (state.state.toLowerCase().includes(q) || cities.length > 0) {
            return { ...state, cities };
          }
          return null;
        })
        .filter(Boolean);

      if (country.country.toLowerCase().includes(q) || states.length > 0) {
        return { ...country, states };
      }
      return null;
    })
    .filter(Boolean);
}

const LocationFilter = ({ locations = [], selectedLocations = [], onLocationChange }) => {

  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => ({})); // Start with all collapsed

  // Convert selectedLocations from transformed format if needed
  const initialSelected = React.useMemo(() => {
    if (!selectedLocations) {
      // When no selection is passed (undefined/null), default to all locations selected
      return deepClone(locations);
    }
    if (selectedLocations.length === 0) {
      // Empty selection explicitly passed
      return [];
    }
    // Check if it's in transformed format (has 'children' property)
    if (selectedLocations[0]?.children) {
      return convertFromTransformedFormat(selectedLocations);
    }
    // Already in internal format
    return deepClone(selectedLocations);
  }, [selectedLocations, locations]);

  const [tempSelected, setTempSelected] = useState(initialSelected); // Temporary selection state
  const [appliedSelected, setAppliedSelected] = useState(initialSelected); // Applied/confirmed selections

  // Use tempSelected for all operations instead of selected
  const selected = tempSelected;
  const setSelected = setTempSelected;

  const toggleExpand = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  /* Helper: Generate chip data from appliedSelected */
  const generateChipData = () => {
    const chips = [];

    // Collect all countries
    const countries = appliedSelected.map(c => c.country);
    if (countries.length > 0) {
      const label = countries.length === 1
        ? `Country: ${countries[0]}`
        : `Country: ${countries[0]} + ${countries.length - 1}`;
      chips.push({ type: 'country', label, items: countries });
    }

    // Collect all states
    const states = [];
    appliedSelected.forEach(c => {
      (c.states || []).forEach(s => states.push(s.state));
    });
    if (states.length > 0) {
      const label = states.length === 1
        ? `State: ${states[0]}`
        : `State: ${states[0]} + ${states.length - 1}`;
      chips.push({ type: 'state', label, items: states });
    }

    // Collect all cities
    const cities = [];
    appliedSelected.forEach(c => {
      (c.states || []).forEach(s => {
        (s.cities || []).forEach(city => cities.push(city.name));
      });
    });
    if (cities.length > 0) {
      const label = cities.length === 1
        ? `City: ${cities[0]}`
        : `City: ${cities[0]} + ${cities.length - 1}`;
      chips.push({ type: 'city', label, items: cities });
    }

    // Collect all depots
    const depots = [];
    appliedSelected.forEach(c => {
      (c.states || []).forEach(s => {
        (s.cities || []).forEach(city => {
          (city.depots || []).forEach(depot => depots.push(depot));
        });
      });
    });
    if (depots.length > 0) {
      const label = depots.length === 1
        ? `Depot: ${depots[0]}`
        : `Depot: ${depots[0]} + ${depots.length - 1}`;
      chips.push({ type: 'depot', label, items: depots });
    }

    return chips;
  };

  const chipData = generateChipData();

  /* Helper: find original nodes */
  const findOriginalCountry = (countryName) =>
    locations.find((c) => c.country === countryName);
  const findOriginalState = (countryName, stateName) =>
    findOriginalCountry(countryName)?.states?.find(
      (s) => s.state === stateName
    );
  const findOriginalCity = (countryName, stateName, cityName) =>
    findOriginalState(countryName, stateName)?.cities?.find(
      (c) => c.name === cityName
    );

  /* ---------- Toggle functions ---------- */

  const toggleCountry = (countryName) =>
    setSelected((prev) => {
      const exists = prev.find((c) => c.country === countryName);
      if (exists) {
        return prev.filter((c) => c.country !== countryName);
      } else {
        const orig = findOriginalCountry(countryName);
        if (!orig) return prev;
        return [...prev, deepClone(orig)];
      }
    });

  const toggleState = (countryName, stateName) =>
    setSelected((prev) => {
      const copy = deepClone(prev);
      const countryIndex = copy.findIndex((c) => c.country === countryName);
      const origState = findOriginalState(countryName, stateName);
      if (countryIndex === -1) {
        copy.push({ country: countryName, states: [deepClone(origState)] });
        return copy;
      }
      const countrySel = copy[countryIndex];
      countrySel.states = countrySel.states || [];
      const stateIndex = countrySel.states.findIndex(
        (s) => s.state === stateName
      );
      if (stateIndex > -1) {
        countrySel.states.splice(stateIndex, 1);
        if (!countrySel.states.length) {
          copy.splice(countryIndex, 1);
        }
      } else {
        countrySel.states.push(deepClone(origState));
      }
      return copy;
    });

  const toggleCity = (countryName, stateName, cityName) =>
    setSelected((prev) => {
      const copy = deepClone(prev);
      let countrySel = copy.find((c) => c.country === countryName);
      if (!countrySel) {
        const origCity = findOriginalCity(countryName, stateName, cityName);
        copy.push({
          country: countryName,
          states: [{ state: stateName, cities: [deepClone(origCity)] }],
        });
        return copy;
      }
      countrySel.states = countrySel.states || [];
      let stateSel = countrySel.states.find((s) => s.state === stateName);
      if (!stateSel) {
        const origCity = findOriginalCity(countryName, stateName, cityName);
        countrySel.states.push({
          state: stateName,
          cities: [deepClone(origCity)],
        });
        return copy;
      }
      stateSel.cities = stateSel.cities || [];
      const cityIndex = stateSel.cities.findIndex((c) => c.name === cityName);
      if (cityIndex > -1) {
        stateSel.cities.splice(cityIndex, 1);
        if (!stateSel.cities.length) {
          countrySel.states = countrySel.states.filter(
            (s) => s.state !== stateName
          );
          if (!countrySel.states.length) {
            const idx = copy.findIndex((c) => c.country === countryName);
            if (idx > -1) copy.splice(idx, 1);
          }
        }
      } else {
        const origCity = findOriginalCity(countryName, stateName, cityName);
        stateSel.cities.push(deepClone(origCity));
      }
      return copy;
    });

  const toggleDepot = (countryName, stateName, cityName, depotName) =>
    setSelected((prev) => {
      const copy = deepClone(prev);
      let countrySel = copy.find((c) => c.country === countryName);
      if (!countrySel) {
        copy.push({
          country: countryName,
          states: [
            {
              state: stateName,
              cities: [{ name: cityName, depots: [depotName] }],
            },
          ],
        });
        return copy;
      }
      countrySel.states = countrySel.states || [];
      let stateSel = countrySel.states.find((s) => s.state === stateName);
      if (!stateSel) {
        countrySel.states.push({
          state: stateName,
          cities: [{ name: cityName, depots: [depotName] }],
        });
        return copy;
      }
      stateSel.cities = stateSel.cities || [];
      let citySel = stateSel.cities.find((c) => c.name === cityName);
      if (!citySel) {
        stateSel.cities.push({ name: cityName, depots: [depotName] });
        return copy;
      }
      citySel.depots = citySel.depots || [];
      const depotIndex = citySel.depots.findIndex((d) => d === depotName);
      if (depotIndex > -1) {
        citySel.depots.splice(depotIndex, 1);
        if (!citySel.depots.length) {
          stateSel.cities = stateSel.cities.filter((c) => c.name !== cityName);
          if (!stateSel.cities.length) {
            countrySel.states = countrySel.states.filter(
              (s) => s.state !== stateName
            );
            if (!countrySel.states.length) {
              const idx = copy.findIndex((c) => c.country === countryName);
              if (idx > -1) copy.splice(idx, 1);
            }
          }
        }
      } else {
        citySel.depots.push(depotName);
      }
      return copy;
    });

  /* ---------- Selection-check helpers ---------- */

  const findSelCountry = (countryName) =>
    selected.find((c) => c.country === countryName);
  const findSelState = (countryName, stateName) =>
    findSelCountry(countryName)?.states?.find((s) => s.state === stateName);
  const findSelCity = (countryName, stateName, cityName) =>
    findSelState(countryName, stateName)?.cities?.find(
      (c) => c.name === cityName
    );

  const isCityFullySelected = (countryName, stateName, city) => {
    const selCity = findSelCity(countryName, stateName, city.name);
    if (!selCity) return false;
    const origDepots = city.depots || [];
    return origDepots.every((d) => selCity.depots?.includes(d));
  };

  const isCityPartiallySelected = (countryName, stateName, city) => {
    const selCity = findSelCity(countryName, stateName, city.name);
    if (!selCity) return false;
    const selCount = selCity.depots?.length || 0;
    return selCount > 0 && !isCityFullySelected(countryName, stateName, city);
  };

  const isStateFullySelected = (countryName, state) => {
    const selState = findSelState(countryName, state.state);
    if (!selState) return false;
    return state.cities.every((city) =>
      isCityFullySelected(countryName, state.state, city)
    );
  };

  const isStatePartiallySelected = (countryName, state) => {
    const selState = findSelState(countryName, state.state);
    if (!selState) return false;
    const anySelected = state.cities.some(
      (city) =>
        isCityFullySelected(countryName, state.state, city) ||
        isCityPartiallySelected(countryName, state.state, city)
    );
    return anySelected && !isStateFullySelected(countryName, state);
  };

  const isCountryFullySelected = (country) => {
    const selCountry = findSelCountry(country.country);
    if (!selCountry) return false;
    return country.states.every((s) =>
      isStateFullySelected(country.country, s)
    );
  };

  const isCountryPartiallySelected = (country) => {
    const selCountry = findSelCountry(country.country);
    if (!selCountry) return false;
    const anySelected = country.states.some(
      (s) =>
        isStateFullySelected(country.country, s) ||
        isStatePartiallySelected(country.country, s)
    );
    return anySelected && !isCountryFullySelected(country);
  };

  /* ---------- Render helpers ---------- */
  const renderDepots = (countryName, stateName, city) =>
    (city.depots || []).map((depot) => {
      const checked = !!findSelCity(
        countryName,
        stateName,
        city.name
      )?.depots?.includes(depot);
      return (
        <StyledListItem
          key={`${countryName}-${stateName}-${city.name}-${depot}`}
          dense
          sx={{ pl: 8 }}
        >
          <ListItemIcon sx={{ minWidth: "auto" }}>
            <StyledFormControlLabel
              hasChildren={false}
              isLastNode={true}
              control={
                <CustomCheckbox
                  edge="start"
                  checked={checked}
                  onChange={() =>
                    toggleDepot(countryName, stateName, city.name, depot)
                  }
                />
              }
              label={
                <Typography
                  variant="fw400s12c494949"
                  sx={{ lineHeight: "24px" }}
                >
                  {depot}
                </Typography>
              }
            />
          </ListItemIcon>
        </StyledListItem>
      );
    });

  const renderCities = (countryName, state) =>
    state.cities.map((city) => {
      const key = `${countryName}-${state.state}-${city.name}`;
      const checked = isCityFullySelected(countryName, state.state, city);
      const indeterminate = isCityPartiallySelected(
        countryName,
        state.state,
        city
      );

      return (
        <React.Fragment key={key}>
          <StyledListItem dense sx={{ pl: 4 }}>
            {city.depots.length > 0 && (
              <IconButton
                size="small"
                onClick={() => toggleExpand(key)}
                sx={{ p: 0 }}
              >
                <ExpandCollapseIcon isExpanded={expanded[key]} />
              </IconButton>
            )}
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <StyledFormControlLabel
                hasChildren={city.depots.length > 0}
                isLastNode={false}
                control={
                  <CustomCheckbox
                    edge="start"
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={() =>
                      toggleCity(countryName, state.state, city.name)
                    }
                  />
                }
                label={
                  <Typography
                    variant="fw400s12c494949"
                    sx={{ lineHeight: "24px" }}
                  >
                    {city.name}
                  </Typography>
                }
              />
            </ListItemIcon>
          </StyledListItem>

          {city.depots.length > 0 && (
            <Collapse in={expanded[key]} timeout="auto" unmountOnExit>
              <List disablePadding>
                {renderDepots(countryName, state.state, city)}
              </List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  const renderStates = (country) =>
    country.states.map((state) => {
      const key = `${country.country}-${state.state}`;
      const checked = isStateFullySelected(country.country, state);
      const indeterminate = isStatePartiallySelected(country.country, state);

      return (
        <React.Fragment key={key}>
          <StyledListItem dense sx={{ pl: 2 }}>
            {state.cities.length > 0 && (
              <IconButton
                size="small"
                onClick={() => toggleExpand(key)}
                sx={{ p: 0 }}
              >
                <ExpandCollapseIcon isExpanded={expanded[key]} />
              </IconButton>
            )}
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <StyledFormControlLabel
                hasChildren={state.cities.length > 0}
                isLastNode={false}
                control={
                  <CustomCheckbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={() => toggleState(country.country, state.state)}
                  />
                }
                label={
                  <Typography
                    variant="fw400s12c494949"
                    sx={{ lineHeight: "24px" }}
                  >
                    {state.state}
                  </Typography>
                }
              />
            </ListItemIcon>
          </StyledListItem>

          <Collapse in={expanded[key]} timeout="auto" unmountOnExit>
            <List disablePadding>{renderCities(country.country, state)}</List>
          </Collapse>
        </React.Fragment>
      );
    });

  /* ---------- UI ---------- */

  const filteredLocations = useMemo(
    () => filterLocations(locations, search),
    [locations, search]
  );

  // Auto-expand all nodes that contain search matches
  useEffect(() => {
    if (!search) {
      // When search is cleared, collapse all
      setExpanded({});
      return;
    }

    const newExpanded = {};

    // Expand all nodes that contain matches
    filteredLocations.forEach((country) => {
      // Expand country level
      newExpanded[country.country] = true;

      country.states.forEach((state) => {
        // Expand state level
        const stateKey = `${country.country}-${state.state}`;
        newExpanded[stateKey] = true;

        state.cities.forEach((city) => {
          // Expand city level if it has depots
          if (city.depots.length > 0) {
            const cityKey = `${country.country}-${state.state}-${city.name}`;
            newExpanded[cityKey] = true;
          }
        });
      });
    });

    setExpanded(newExpanded);
  }, [search, filteredLocations]);

  const handleClearSelection = () => {
    setTempSelected([]);
  };

  const handleApply = () => {
    setAppliedSelected(tempSelected);
    if (onLocationChange) {
      // Convert internal format to the new transformed format (with children)
      const transformedSelection = convertToTransformedFormat(tempSelected);
      onLocationChange(transformedSelection);
    }
  };

  useEffect(() => {
    setExpanded(() => ({})); // Reset to collapsed when locations change

    let newSelection;
    if (!selectedLocations) {
      // When no selection is passed (undefined/null), default to all locations selected
      newSelection = deepClone(locations);
    } else if (selectedLocations.length === 0) {
      // Empty selection explicitly passed
      newSelection = [];
    } else if (selectedLocations[0]?.children) {
      // Convert from transformed format
      newSelection = convertFromTransformedFormat(selectedLocations);
    } else {
      // Already in internal format
      newSelection = deepClone(selectedLocations);
    }

    setTempSelected(newSelection);
    setAppliedSelected(newSelection);
  }, [locations, selectedLocations]);

  return (
    <Accordion
      defaultExpanded
      sx={{
        boxShadow: "none",
        my: 0,
        borderBottom: '1px solid #E6E6E6',
        "&:before": { display: "none" },
        "&.Mui-expanded": {
          my: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          paddingTop: '18px',
          paddingBottom: '18px',
          alignItems: 'flex-start',
          "&.Mui-expanded": {
            minHeight: "auto",
          },
          "& .MuiAccordionSummary-content": {
            margin: 0,
            alignItems: "flex-start",
            gap: 1,
          },
        }}
      >
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
          <Typography variant="fw400s14c000000" sx={{ lineHeight: '16px', color: "#1C1C1C", mr: 1 }}>
            Location
          </Typography>
          {chipData.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
              onClick={(e) => e.stopPropagation()} // prevent accordion toggle
            >
              {chipData.map((chip) => (
                <PrimaryChip
                  key={chip.type}
                  label={chip.label}
                  tooltipTitle={toTitleCase(chip.type) + ': ' + chip.items.join(", ")}
                  tooltipPlacement="bottom"
                  showDelete={false}
                />
              ))}
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Location"
          />

          <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
            <List disablePadding>
              {filteredLocations.map((country) => {
                const key = country.country;
                const checked = isCountryFullySelected(country);
                const indeterminate = isCountryPartiallySelected(country);
                return (
                  <React.Fragment key={key}>
                    <StyledListItem dense>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(key)}
                        sx={{ p: 0 }}
                      >
                        <ExpandCollapseIcon isExpanded={expanded[key]} />
                      </IconButton>
                      <ListItemIcon sx={{ minWidth: "auto" }}>
                        <StyledFormControlLabel
                          hasChildren={country.states.length > 0}
                          isLastNode={false}
                          control={
                            <CustomCheckbox
                              checked={checked}
                              indeterminate={indeterminate}
                              onChange={() => toggleCountry(country.country)}
                            />
                          }
                          label={
                            <Typography
                              variant="fw400s12c494949"
                              sx={{ lineHeight: "24px" }}
                            >
                              {country.country}
                            </Typography>
                          }
                        />
                      </ListItemIcon>
                    </StyledListItem>
                    <Collapse in={expanded[key]} timeout="auto" unmountOnExit>
                      <List disablePadding>{renderStates(country)}</List>
                    </Collapse>
                  </React.Fragment>
                );
              })}
            </List>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              mt: 1,
            }}
          >
            <Typography
              variant="fw500s12lh24c6D72F6"
              sx={{
                cursor: tempSelected.length > 0 ? "pointer" : "not-allowed",
                color: tempSelected.length > 0 ? "#6D72F6" : "#8F8F8F",
              }}
              onClick={() =>
                tempSelected.length > 0 ? handleClearSelection() : null
              }
            >
              Clear Selection
            </Typography>
            <PrimaryFormActionButton
              onClick={handleApply}
              sx={{py: 0.75, px: 2, fontSize: '12px'}}
            >
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default LocationFilter;
